import type { ThemeOverrides } from "./types";

const DEFAULT_THEME: Required<ThemeOverrides> = {
  backgroundColor: "#ffffff",
  textColor: "#111827",
  borderColor: "#e5e7eb",
  borderRadius: "0.75rem",
  maxWidth: "34rem",
};

function withTheme(theme?: ThemeOverrides): Required<ThemeOverrides> {
  return {
    ...DEFAULT_THEME,
    ...theme,
  };
}

export function getPopoverStyles(theme?: ThemeOverrides): string {
  const resolvedTheme = withTheme(theme);

  return `
    :host {
      all: initial;
    }

    *, *::before, *::after {
      box-sizing: border-box;
    }

    .card {
      --preview-bg: ${resolvedTheme.backgroundColor};
      --preview-text: ${resolvedTheme.textColor};
      --preview-border: ${resolvedTheme.borderColor};
      --preview-radius: ${resolvedTheme.borderRadius};
      --preview-max-width: ${resolvedTheme.maxWidth};
      --preview-muted: #6b7280;
      --preview-muted-light: #4b5563;
      --preview-placeholder: #f3f4f6;
      color: var(--preview-text);
      background: var(--preview-bg);
      border: 1px solid var(--preview-border);
      border-radius: var(--preview-radius);
      box-shadow: 0 18px 48px rgba(15, 23, 42, 0.18);
      display: flex;
      gap: 1.25rem;
      width: min(var(--preview-max-width), calc(100vw - 2rem));
      max-width: var(--preview-max-width);
      min-width: 0;
      padding: 1.25rem;
      font-family: ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
      font-size: 17px;
      line-height: 1.35;
    }

    .media {
      flex-shrink: 0;
      border-radius: calc(var(--preview-radius) - 0.25rem);
      overflow: hidden;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .media img {
      max-width: 11rem;
      max-height: 14rem;
      width: auto;
      height: auto;
      object-fit: contain;
      display: block;
    }

    .content {
      min-width: 0;
      flex: 1;
    }

    .title {
      margin: 0;
      color: var(--preview-text);
      font-size: 1.125rem;
      font-weight: 600;
      line-height: 1.25;
    }

    .subtitle {
      margin: 0.25rem 0 0;
      color: var(--preview-muted);
      font-size: 0.9375rem;
      line-height: 1.35;
    }

    .description {
      margin: 0.375rem 0 0;
      color: var(--preview-muted-light);
      font-size: 0.9375rem;
      display: -webkit-box;
      overflow: hidden;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 6;
    }

    .skeleton {
      position: relative;
      overflow: hidden;
      background: linear-gradient(
        90deg,
        rgba(229, 231, 235, 0.65) 0%,
        rgba(243, 244, 246, 0.95) 50%,
        rgba(229, 231, 235, 0.65) 100%
      );
      background-size: 200% 100%;
      animation: shimmer 1.25s linear infinite;
    }

    .skeleton.media {
      width: 11rem;
      height: 14rem;
      background-color: var(--preview-placeholder);
    }

    .skeleton-line {
      border-radius: 999px;
      height: 0.75rem;
      margin-top: 0.5rem;
      width: 100%;
    }

    .skeleton-line:first-child {
      margin-top: 0;
      width: 80%;
      height: 0.875rem;
    }

    .skeleton-line.short {
      width: 65%;
    }

    @keyframes shimmer {
      0% {
        background-position: 200% 0;
      }

      100% {
        background-position: -200% 0;
      }
    }

    @media (prefers-reduced-motion: reduce) {
      .skeleton {
        animation: none;
      }
    }
  `;
}
